import { ThinkingLevel } from 'firebase/ai';
import {
  createElementNode,
  createEmptyEditorDocument,
  extractEditorDocument,
  findNodeById,
  insertNode,
  isTextNode,
  moveNode,
  removeNodeById,
  sanitizeNode,
  updateNodeById,
  validateEditorDocument,
} from '../../../NewBlogSystem/utilities/htmlEditorUtils.js';
import { validateCollectionBindings } from '../../../NewBlogSystem/utilities/htmlCollectionUtils.js';
import {
  canPortfolioElementNestChild,
  canPortfolioElementNestText,
  PORTFOLIO_TAGS,
} from '../../../NewBlogSystem/utilities/portfolioElementRegistry.jsx';
import {
  FONT_THEME_CLASS,
  GOOGLE_FONT_AI_CONTEXT,
  HEADING_FONT_PROPERTY,
  normalizeAiFontDefinitions,
} from '../../../NewBlogSystem/utilities/googleFonts.js';
import {
  HTML_EDITOR_EDIT_MAX_OUTPUT_TOKENS,
  HTML_EDITOR_FOCUSED_MAX_OUTPUT_TOKENS,
  HTML_EDITOR_MAX_OUTPUT_TOKENS,
  HTML_EDITOR_PARTIAL_MAX_OUTPUT_TOKENS,
  redactBase64FromAiPrompt,
  requestEditorJsonWithRetry,
} from './htmlAiRequest.js';
import promptConfig from '../config/htmlAiPromptConfig.json';
import { ANIMATION_AI_CONTEXT } from '../../../NewBlogSystem/utilities/animationClasses.js';
import { repairHeadingContrast } from './aiHeadingContrast.js';

export {
  HTML_EDITOR_AI_MODEL_NAME,
  HTML_EDITOR_AI_MODEL_NAMES,
  HTML_EDITOR_FALLBACK_AI_MODEL_NAME,
  HTML_EDITOR_MAX_OUTPUT_TOKENS,
  HTML_EDITOR_EDIT_MAX_OUTPUT_TOKENS,
  HTML_EDITOR_FOCUSED_MAX_OUTPUT_TOKENS,
  HTML_EDITOR_PARTIAL_MAX_OUTPUT_TOKENS,
  redactBase64FromAiPrompt,
} from './htmlAiRequest.js';

const replacePromptTokens = (value, tokens = {}) => String(value || '').replace(
  /\{\{([A-Z_]+)\}\}/g,
  (match, tokenName) => (
    Object.prototype.hasOwnProperty.call(tokens, tokenName) ? tokens[tokenName] : match
  )
);

const promptRules = (rules = [], tokens = {}) => rules
  .map((rule) => `- ${replacePromptTokens(rule, tokens)}`)
  .join('\n');

const promptExample = (example, tokens = {}) => replacePromptTokens(
  JSON.stringify(example, null, 2),
  tokens
);

const schemaTokens = {
  SAFE_TAGS: [...PORTFOLIO_TAGS].join(', '),
  GOOGLE_FONTS: GOOGLE_FONT_AI_CONTEXT,
  FONT_THEME_CLASS,
  HEADING_FONT_PROPERTY,
  ANIMATION_CLASSES: ANIMATION_AI_CONTEXT,
};

const AI_ELEMENT_NESTING_PROMPT = promptRules(promptConfig.nestingRules);

export const HTML_EDITOR_SCHEMA_PROMPT = [
  promptConfig.schema.intro.join('\n\n'),
  promptExample(promptConfig.schema.example),
  promptConfig.labels.rules,
  promptRules(promptConfig.schema.rules, schemaTokens),
  AI_ELEMENT_NESTING_PROMPT,
].join('\n\n');

const safeStringify = (value, spacing = 2) => {
  try {
    return JSON.stringify(redactBase64FromAiPrompt(value), null, spacing);
  } catch (_) {
    return 'null';
  }
};

const cloneJson = (value) => JSON.parse(JSON.stringify(value));

const getImageToken = (imageId) => `{{image:${imageId}}}`;

const getStoredImageId = (image) => {
  const imageId = image?.id ?? image?.imageId;
  return imageId === undefined || imageId === null ? '' : String(imageId).trim();
};

const addRecordId = (target, value) => {
  const recordId = value === undefined || value === null ? '' : String(value).trim();
  if (recordId) target.add(recordId);
};

const collectExistingCollectionIds = (node, projectIds, skillIds) => {
  if (!node || typeof node !== 'object') return;
  addRecordId(projectIds, node.attributes?.['data-project-id']);
  addRecordId(skillIds, node.attributes?.['data-skill-id']);
  addRecordId(skillIds, node.attributes?.['data-project-skill-id']);
  (node.children || []).forEach((child) => {
    collectExistingCollectionIds(child, projectIds, skillIds);
  });
};

export const buildAiCollectionValidationContext = (
  sourceDocument,
  { projects = [], skills = [] } = {}
) => {
  const projectIds = new Set();
  const skillIds = new Set();
  collectExistingCollectionIds(sourceDocument?.root, projectIds, skillIds);
  projects.forEach((project) => addRecordId(projectIds, project?.id));
  skills.forEach((skill) => addRecordId(skillIds, skill?.id));
  return {
    projectIds: [...projectIds],
    skillIds: [...skillIds],
  };
};

const collectHydratedDocumentImages = (node, images = []) => {
  if (!node || typeof node !== 'object') return images;

  const imageId = String(node.attributes?.['data-image-id'] || '').trim();
  const source = node.attributes?.src;
  if (node.type === 'img' && imageId && typeof source === 'string' && source.startsWith('data:image/')) {
    images.push({
      id: imageId,
      base64: source,
      alt: node.attributes?.alt || '',
    });
  }

  (node.children || []).forEach((child) => collectHydratedDocumentImages(child, images));
  return images;
};

const mergeImageContext = (documentRoot, images = []) => {
  const imagesById = new Map();
  [...collectHydratedDocumentImages(documentRoot), ...images].forEach((image) => {
    const imageId = getStoredImageId(image);
    if (imageId) imagesById.set(imageId, { ...image, id: imageId });
  });
  return [...imagesById.values()];
};

const MAX_CONTEXT_TEXT_LENGTH = 1200;

const compactText = (value, maxLength = MAX_CONTEXT_TEXT_LENGTH) => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}...` : text;
};

const firstText = (...values) => {
  const value = values.find((entry) => (
    (typeof entry === 'string' || typeof entry === 'number') && compactText(entry)
  ));
  return compactText(value);
};

const compactObject = (value) => Object.fromEntries(
  Object.entries(value).filter(([, entry]) => entry !== '' && entry !== undefined && entry !== null)
);

const compactStringList = (value, limit = 12) => {
  const entries = Array.isArray(value) ? value : value ? [value] : [];
  return [...new Set(entries.map((entry) => (
    typeof entry === 'object' ? firstText(entry.name, entry.title, entry.label) : compactText(entry, 120)
  )).filter(Boolean))].slice(0, limit);
};

const summarizeImages = (images = []) => images.map((image) => compactObject({
  id: compactText(getStoredImageId(image), 160),
  description: compactText(firstText(image?.description, image?.alt), 320),
}));

export const buildPortfolioAiContext = ({
  portfolioTitle = '',
  projects = [],
  skills = [],
  jobs = [],
  images = [],
} = {}) => {
  const projectRecords = Array.isArray(projects) ? projects : [];
  const skillRecords = Array.isArray(skills) ? skills : [];
  const jobRecords = Array.isArray(jobs) ? jobs : [];
  const imageRecords = Array.isArray(images) ? images : [];

  return {
    portfolio: {
      title: compactText(portfolioTitle, 240),
      projectCount: projectRecords.length,
      skillCount: skillRecords.length,
      targetJobCount: jobRecords.length,
      imageCount: imageRecords.length,
    },
    projects: projectRecords.slice(0, 12).map((project) => compactObject({
      id: compactText(project?.id, 160),
      title: firstText(project?.title, project?.projectTitle, project?.name),
      overview: firstText(project?.overview, project?.description, project?.summary),
      url: firstText(project?.projectUrl, project?.linkUrl, project?.url),
      technologies: compactStringList(project?.technologies || project?.skills || project?.tags),
      skillIds: compactStringList(project?.skillsUsed),
      imageIds: compactStringList((Array.isArray(project?.images) ? project.images : []).map((image) => (
        typeof image === 'string' ? image : image?.imageId || image?.id
      ))),
    })),
    skills: skillRecords.slice(0, 30).map((skill) => compactObject({
      id: compactText(skill?.id, 160),
      name: firstText(skill?.name, skill?.title),
      description: firstText(skill?.description, skill?.overview),
      rating: Number.isFinite(Number(skill?.rating)) ? Math.max(0, Math.min(5, Number(skill.rating))) : undefined,
    })),
    targetJobs: jobRecords.slice(0, 12).map((job) => compactObject({
      purpose: 'portfolio-optimization-target',
      id: compactText(job?.id, 160),
      title: firstText(job?.title, job?.jobTitle, job?.role),
      company: firstText(job?.company, job?.employer, job?.organization),
      description: firstText(job?.description, job?.overview, job?.summary),
      dates: firstText(job?.dates, job?.dateRange, job?.duration),
    })),
    availableImages: summarizeImages(imageRecords),
  };
};

export const repairAiElementNesting = (editorDocument) => {
  const repairChildren = (parentTag, children = []) => children.flatMap((child) => {
    if (!child) return [];
    if (isTextNode(child)) {
      return canPortfolioElementNestText(parentTag) ? [child] : [];
    }
    if (!canPortfolioElementNestChild(parentTag, child.type)) {
      return repairChildren(parentTag, child.children || []);
    }

    return [{
      ...child,
      children: repairChildren(child.type, child.children || []),
    }];
  });

  if (!editorDocument?.root || isTextNode(editorDocument.root)) return editorDocument;
  return {
    ...editorDocument,
    root: {
      ...editorDocument.root,
      children: repairChildren(editorDocument.root.type, editorDocument.root.children || []),
    },
  };
};

export const validateAndRepairEditorJson = (candidate, collectionContext = {}, options = {}) => {
  const rawDocument = candidate?.content?.root ? candidate.content : candidate;
  const validated = validateEditorDocument(rawDocument).document;
  const nestingSafeDocument = repairAiElementNesting(validated);
  const fontSafeDocument = options.approvedFontsOnly
    ? normalizeAiFontDefinitions(nestingSafeDocument)
    : nestingSafeDocument;
  return repairHeadingContrast(validateCollectionBindings(fontSafeDocument, collectionContext));
};

export const hydrateImageTokensInEditorJson = (candidate, images = []) => {
  const imageMap = new Map(
    images
      .map((image) => [getStoredImageId(image), image])
      .filter(([imageId, image]) => imageId && image?.base64)
      .map(([imageId, image]) => [getImageToken(imageId), { ...image, id: imageId }])
  );

  if (imageMap.size === 0 || !candidate) {
    return candidate;
  }

  const nextCandidate = cloneJson(candidate);
  const visitNode = (node) => {
    if (!node || typeof node !== 'object') {
      return;
    }

    if (node.attributes && typeof node.attributes === 'object') {
      const src = node.attributes.src;
      if (typeof src === 'string' && imageMap.has(src)) {
        const image = imageMap.get(src);
        node.attributes.src = image.base64;
        node.attributes['data-image-id'] = image.id;
      }
    }

    if (Array.isArray(node.children)) {
      node.children.forEach(visitNode);
    }
  };

  const roots = [
    nextCandidate?.content?.root,
    nextCandidate?.root,
    nextCandidate?.node,
    ...(Array.isArray(nextCandidate?.children) ? nextCandidate.children : []),
    ...(Array.isArray(nextCandidate?.updates)
      ? nextCandidate.updates.map((update) => update?.node)
      : []),
    ...(Array.isArray(nextCandidate?.insertions)
      ? nextCandidate.insertions.map((insertion) => insertion?.node)
      : []),
  ].filter(Boolean);
  roots.forEach(visitNode);
  return nextCandidate;
};

const dehydrateImageSourcesForPrompt = (value, images = []) => {
  if (!value) {
    return value;
  }

  const base64ToToken = new Map(
    images
      .map((image) => [getStoredImageId(image), image])
      .filter(([imageId, image]) => imageId && image?.base64)
      .map(([imageId, image]) => [image.base64, getImageToken(imageId)])
  );
  const nextValue = cloneJson(value);

  const visitNode = (node) => {
    if (!node || typeof node !== 'object') {
      return;
    }

    if (node.attributes && typeof node.attributes.src === 'string') {
      const src = node.attributes.src;
      const imageId = String(node.attributes['data-image-id'] || '').trim();
      if (imageId && src.startsWith('data:image/')) {
        node.attributes.src = getImageToken(imageId);
      } else if (base64ToToken.has(src)) {
        node.attributes.src = base64ToToken.get(src);
      } else if (src.startsWith('data:image/')) {
        node.attributes.src = '[stored-base64-image-omitted]';
      }
    }

    if (Array.isArray(node.children)) {
      node.children.forEach(visitNode);
    }
  };

  const root = nextValue?.content?.root || nextValue?.root || nextValue;
  visitNode(root);
  return nextValue;
};

export const buildFullPageGenerationPrompt = ({
  prompt,
  currentDocument,
  portfolioTitle,
  images = [],
  projects = [],
  skills = [],
  jobs = [],
  retry = false,
  outputMode = 'auto',
} = {}) => {
  const hasCurrentContent = Array.isArray(currentDocument?.root?.children)
    && currentDocument.root.children.length > 0;
  const useCompactPatch = outputMode === 'compact'
    || (outputMode === 'auto' && hasCurrentContent);
  const commonContext = `
${promptConfig.labels.userRequest}
${compactText(prompt, 4000)}

${promptConfig.labels.currentDocument}
${safeStringify(dehydrateImageSourcesForPrompt(currentDocument, images), 0)}

${promptConfig.labels.portfolioContext}
${safeStringify(buildPortfolioAiContext({ portfolioTitle, projects, skills, jobs, images }), 0)}
`;

  if (useCompactPatch) {
    return `
${HTML_EDITOR_SCHEMA_PROMPT}

${commonContext}

${promptConfig.globalEdit.task}

${promptConfig.labels.returnExactly}
${promptExample(promptConfig.globalEdit.example)}

${promptConfig.labels.rules}
${promptRules(promptConfig.globalEdit.rules, {
      SAFE_TAGS: [...PORTFOLIO_TAGS].join(', '),
      ANIMATION_CLASSES: ANIMATION_AI_CONTEXT,
    })}
${AI_ELEMENT_NESTING_PROMPT}
${retry ? `- ${promptConfig.globalEdit.retryRule}` : ''}
`;
  }

  return `
${HTML_EDITOR_SCHEMA_PROMPT}

${commonContext}

${hasCurrentContent ? promptConfig.fullPage.regenerateTask : promptConfig.fullPage.generateTask}
${promptRules(promptConfig.fullPage.rules)}
${retry ? `- ${promptConfig.fullPage.retryRule}` : ''}
`;
};

const isCompactGlobalPatch = (candidate) => (
  candidate
  && typeof candidate === 'object'
  && !candidate.root
  && !candidate.content?.root
  && (
    Array.isArray(candidate.updates)
    || Array.isArray(candidate.insertions)
    || Array.isArray(candidate.removals)
    || Array.isArray(candidate.moves)
    || candidate.classes
    || Array.isArray(candidate.removeClasses)
  )
);

const requestPortfolioDocumentChange = async ({
  prompt,
  portfolio,
  images = [],
  projects = [],
  skills = [],
  jobs = [],
  fallbackTitle = 'AI HTML Draft',
  onRetry,
  outputMode,
} = {}) => {
  const currentDocument = extractEditorDocument(portfolio);
  const effectiveImages = mergeImageContext(currentDocument?.root, images);
  const useCompactPatch = outputMode === 'compact';
  const generated = await requestEditorJsonWithRetry({
    buildPrompt: (retry) => buildFullPageGenerationPrompt({
      prompt,
      currentDocument,
      portfolioTitle: fallbackTitle,
      images,
      projects,
      skills,
      jobs,
      retry,
      outputMode,
    }),
    maxOutputTokens: useCompactPatch
      ? HTML_EDITOR_EDIT_MAX_OUTPUT_TOKENS
      : HTML_EDITOR_MAX_OUTPUT_TOKENS,
    generationConfig: useCompactPatch
      ? { thinkingConfig: { thinkingLevel: ThinkingLevel.LOW } }
      : {},
    fallbackGenerationConfig: {
      thinkingConfig: { thinkingLevel: ThinkingLevel.MINIMAL },
    },
    onRetry,
  });

  const hydratedResult = hydrateImageTokensInEditorJson(generated, effectiveImages);
  if (useCompactPatch && !isCompactGlobalPatch(hydratedResult)) {
    throw new Error('The AI assistant did not return a usable set of page changes. Try a more specific request.');
  }
  if (
    !useCompactPatch
    && (
      isCompactGlobalPatch(hydratedResult)
      || (!hydratedResult?.root && !hydratedResult?.content?.root)
    )
  ) {
    throw new Error('The AI assistant did not return a complete page. Try a shorter or more specific description.');
  }

  const nextDocument = useCompactPatch
    ? applyGlobalEditorPatch(currentDocument, hydratedResult)
    : hydratedResult;

  return validateAndRepairEditorJson(
    nextDocument,
    buildAiCollectionValidationContext(currentDocument, { projects, skills }),
    { approvedFontsOnly: true }
  );
};

// Existing pages always use bounded operations. Full documents are reserved for
// initial generation and the user's explicit regeneration command.
export const editPortfolioWithCompactPatch = (options = {}) => (
  requestPortfolioDocumentChange({ ...options, outputMode: 'compact' })
);

export const generateFullPageStructure = (options = {}) => (
  requestPortfolioDocumentChange({ ...options, outputMode: 'full' })
);

export const buildAiPreviewRefinementRequest = ({
  request,
  previousRequests = [],
} = {}) => {
  const history = previousRequests
    .slice(-4)
    .map((entry) => compactText(entry, 300))
    .filter(Boolean);

  return [
    promptConfig.previewRefinement.intro,
    history.length
      ? `${promptConfig.previewRefinement.historyLabel}\n${history.map((entry) => `- ${entry}`).join('\n')}`
      : '',
    `${promptConfig.previewRefinement.requestLabel}\n${compactText(request, 1200)}`,
    promptRules(promptConfig.previewRefinement.rules),
  ].filter(Boolean).join('\n\n');
};

const summarizeNodeText = (node, remaining = 240) => {
  if (!node || remaining <= 0) return '';
  if (node.type === 'text') return compactText(node.content, remaining);
  return compactText(
    (node.children || []).map((child) => summarizeNodeText(child, remaining)).filter(Boolean).join(' '),
    remaining
  );
};

const subtreeContainsTag = (node, tagNames) => {
  if (!node || typeof node !== 'object') return false;
  if (tagNames.has(node.type)) return true;
  return (node.children || []).some((child) => subtreeContainsTag(child, tagNames));
};

export const buildAiContextDecisionPrompt = ({
  prompt,
  task = 'portfolio edit',
  selectedNode = null,
} = {}) => `
${promptConfig.contextDecision.responseInstruction}

${replacePromptTokens(promptConfig.contextDecision.question, {
    TASK: compactText(task, 80),
  })}

${promptConfig.labels.userRequest}
${compactText(prompt, 800)}

${promptConfig.labels.selectedSummary}
${selectedNode ? safeStringify({
    type: compactText(selectedNode.type, 40),
    classes: compactStringList(selectedNode.attributes?.class, 8),
    text: summarizeNodeText(selectedNode, 240),
    containsImage: subtreeContainsTag(selectedNode, new Set(['img'])),
    containsProjects: subtreeContainsTag(selectedNode, new Set(['projects'])),
    containsSkills: subtreeContainsTag(selectedNode, new Set(['skills'])),
  }, 0) : promptConfig.labels.emptySelection}

${promptRules(promptConfig.contextDecision.rules)}
`;

export const normalizeAiContextDecision = (candidate = {}) => ({
  includeImages: candidate?.includeImages === true,
  includeProjects: candidate?.includeProjects === true,
  includeSkills: candidate?.includeSkills === true,
  modifyProjects: candidate?.modifyProjects === true,
  modifySkills: candidate?.modifySkills === true,
});

export const FULL_PAGE_AI_CONTEXT_DECISION = Object.freeze({
  includeImages: true,
  includeProjects: true,
  includeSkills: true,
});

export const decideAiPortfolioContext = async ({
  prompt,
  task,
  selectedNode,
} = {}) => {
  const result = await requestEditorJsonWithRetry({
    buildPrompt: () => buildAiContextDecisionPrompt({ prompt, task, selectedNode }),
    maxOutputTokens: 160,
    generationConfig: {
      thinkingConfig: { thinkingLevel: ThinkingLevel.MINIMAL },
    },
  });
  return normalizeAiContextDecision(result);
};

const summarizeSectionInsertionContext = (selectedNode, editorDocument) => ({
  selectedParent: selectedNode ? {
    id: compactText(selectedNode.id, 160),
    type: compactText(selectedNode.type, 40),
    classes: compactStringList(selectedNode.attributes?.class, 12),
  } : null,
  existingSections: (editorDocument?.root?.children || []).slice(0, 16).map((node) => ({
    type: compactText(node?.type, 40),
    classes: compactStringList(node?.attributes?.class, 8),
    summary: summarizeNodeText(node),
  })),
  existingCustomClasses: Object.keys(editorDocument?.classes || {}).slice(0, 32),
});

const buildSectionPortfolioContext = ({ projects, skills, jobs, images }) => {
  const context = buildPortfolioAiContext({ projects, skills, jobs, images });
  return {
    portfolio: context.portfolio,
    projects: context.projects.map((project) => ({
      ...project,
      overview: compactText(project.overview, 360),
    })),
    skills: context.skills.map(({ id, name, rating }) => ({ id, name, rating })),
    targetJobs: context.targetJobs.slice(0, 6).map((job) => ({
      ...job,
      description: compactText(job.description, 360),
    })),
    availableImages: context.availableImages,
  };
};

export const buildChildGenerationPrompt = ({
  prompt,
  selectedNode,
  editorDocument,
  images = [],
  projects = [],
  skills = [],
  jobs = [],
  retry = false,
} = {}) => `
${promptConfig.section.intro.join('\n\n')}

${promptConfig.labels.userRequest}
${compactText(prompt, 2400)}

${promptConfig.labels.insertionContext}
${safeStringify(summarizeSectionInsertionContext(selectedNode, editorDocument), 0)}

${promptConfig.labels.portfolioContext}
${safeStringify(buildSectionPortfolioContext({ projects, skills, jobs, images }), 0)}

${promptConfig.labels.returnExactly}
${promptExample(promptConfig.section.example)}

${promptConfig.labels.rules}
${promptRules(promptConfig.section.rules, {
    SAFE_TAGS: [...PORTFOLIO_TAGS].join(', '),
    ANIMATION_CLASSES: ANIMATION_AI_CONTEXT,
  })}
${AI_ELEMENT_NESTING_PROMPT}
${retry ? `- ${promptConfig.section.retryRule}` : ''}
`;

export const generateChildElements = async ({
  prompt,
  selectedNode,
  editorDocument,
  images = [],
  projects = [],
  skills = [],
  jobs = [],
  onRetry,
} = {}) => {
  const generated = await requestEditorJsonWithRetry({
    buildPrompt: (retry) => buildChildGenerationPrompt({
      prompt,
      selectedNode,
      editorDocument,
      images,
      projects,
      skills,
      jobs,
      retry,
    }),
    maxOutputTokens: HTML_EDITOR_PARTIAL_MAX_OUTPUT_TOKENS,
    fallbackGenerationConfig: {
      thinkingConfig: { thinkingLevel: ThinkingLevel.MINIMAL },
    },
    onRetry,
  });

  const hydrated = hydrateImageTokensInEditorJson(generated, images);
  const generatedChildren = Array.isArray(hydrated?.children)
    ? hydrated.children
    : hydrated?.node ? [hydrated.node] : [];
  if (!generatedChildren.length) {
    throw new Error('The AI assistant did not return a usable section. Try a more specific description.');
  }
  const partialDocument = {
    ...createEmptyEditorDocument(),
    root: createElementNode('div', {}, generatedChildren),
  };
  const repairedPartial = validateAndRepairEditorJson(
    partialDocument,
    buildAiCollectionValidationContext(editorDocument, { projects, skills }),
    { approvedFontsOnly: true }
  );

  const repairedClassDocument = normalizeAiFontDefinitions(validateEditorDocument({
    ...createEmptyEditorDocument(),
    classes: hydrated.classes || {},
  }).document);

  return {
    children: repairedPartial.root.children,
    classes: repairedClassDocument.classes,
  };
};

const collectSubtreeClassNames = (node, result = new Set()) => {
  if (!node || node.type === 'text') return result;
  compactStringList(node.attributes?.class, 24).forEach((className) => result.add(className));
  (node.children || []).forEach((child) => collectSubtreeClassNames(child, result));
  return result;
};

const classDefinitionsForSubtree = (selectedNode, classDefinitions = {}) => {
  const usedClasses = collectSubtreeClassNames(selectedNode);
  if (classDefinitions[FONT_THEME_CLASS]) usedClasses.add(FONT_THEME_CLASS);
  return Object.fromEntries(
    [...usedClasses]
      .filter((className) => Object.prototype.hasOwnProperty.call(classDefinitions, className))
      .slice(0, 32)
      .map((className) => [className, classDefinitions[className]])
  );
};

export const buildFocusedElementEditPrompt = ({
  prompt,
  selectedNode,
  classDefinitions = {},
  images = [],
  projects = [],
  skills = [],
  jobs = [],
  retry = false,
} = {}) => `
${promptConfig.focusedEdit.intro}

${promptConfig.focusedEdit.requestLabel}
${compactText(prompt, 2400)}

${promptConfig.labels.selectedSubtree}
${safeStringify(dehydrateImageSourcesForPrompt(selectedNode, images), 0)}

${promptConfig.labels.reusableClasses}
${safeStringify(classDefinitionsForSubtree(selectedNode, classDefinitions), 0)}

${promptConfig.labels.relevantContext}
${safeStringify(buildSectionPortfolioContext({ projects, skills, jobs, images }), 0)}

${promptConfig.labels.returnExactly}
${promptExample(promptConfig.focusedEdit.example, {
    SELECTED_NODE_ID: selectedNode?.id || 'selected-node',
  })}

${promptConfig.labels.rules}
${promptRules(promptConfig.focusedEdit.rules, {
    SAFE_TAGS: [...PORTFOLIO_TAGS].join(', '),
    ANIMATION_CLASSES: ANIMATION_AI_CONTEXT,
  })}
${AI_ELEMENT_NESTING_PROMPT}
${retry ? `- ${promptConfig.focusedEdit.retryRule}` : ''}
`;

const collectSubtreeIds = (node, result = new Set()) => {
  if (!node || typeof node !== 'object') return result;
  if (node.id) result.add(node.id);
  (node.children || []).forEach((child) => collectSubtreeIds(child, result));
  return result;
};

export const applyFocusedEditorUpdates = (
  editorDocument,
  selectedNodeId,
  generated
) => {
  const document = validateAndRepairEditorJson(editorDocument || createEmptyEditorDocument());
  const selectedNode = findNodeById(document.root, selectedNodeId);
  if (!selectedNode) return document;

  const allowedIds = collectSubtreeIds(selectedNode);
  const updates = Array.isArray(generated?.updates) ? generated.updates.slice(0, 64) : [];
  let nextRoot = document.root;

  updates.forEach((update) => {
    const updateId = String(update?.id || '').trim();
    if (!updateId || !allowedIds.has(updateId)) return;

    const currentNode = findNodeById(nextRoot, updateId);
    if (!currentNode) return;

    let candidate = currentNode;
    if (update.node && typeof update.node === 'object') {
      candidate = sanitizeNode({ ...update.node, id: updateId }) || currentNode;
    } else if (isTextNode(currentNode)) {
      if (Object.prototype.hasOwnProperty.call(update, 'content')) {
        candidate = sanitizeNode({
          ...currentNode,
          content: compactText(update.content, 12000),
        }) || currentNode;
      }
    } else {
      const attributes = { ...(currentNode.attributes || {}) };
      if (update.attributes && typeof update.attributes === 'object') {
        Object.assign(attributes, update.attributes);
      }
      (Array.isArray(update.removeAttributes) ? update.removeAttributes : [])
        .map((name) => String(name || '').trim())
        .filter(Boolean)
        .forEach((name) => delete attributes[name]);

      candidate = sanitizeNode({
        ...currentNode,
        type: update.type || currentNode.type,
        attributes,
      }) || currentNode;
    }

    if (candidate !== currentNode) {
      nextRoot = updateNodeById(nextRoot, updateId, candidate);
    }
  });

  return {
    ...document,
    root: nextRoot,
  };
};

const PATCH_POSITIONS = new Set(['child', 'before', 'after']);

const removeClassAssignments = (node, removedClasses) => {
  if (!node || isTextNode(node)) return node;

  const currentClasses = Array.isArray(node.attributes?.class)
    ? node.attributes.class
    : [];
  const nextClasses = currentClasses.filter((className) => !removedClasses.has(className));
  const children = (node.children || []).map(
    (child) => removeClassAssignments(child, removedClasses)
  );
  const classesChanged = nextClasses.length !== currentClasses.length;
  const childrenChanged = children.some((child, index) => child !== node.children[index]);
  if (!classesChanged && !childrenChanged) return node;

  return {
    ...node,
    attributes: {
      ...(node.attributes || {}),
      class: nextClasses,
    },
    children,
  };
};

export const applyGlobalEditorPatch = (editorDocument, generated) => {
  const currentDocument = validateAndRepairEditorJson(
    editorDocument || createEmptyEditorDocument()
  );

  let nextDocument = applyFocusedEditorUpdates(
    currentDocument,
    currentDocument.root.id,
    generated
  );
  let nextRoot = nextDocument.root;

  (Array.isArray(generated?.removals) ? generated.removals : [])
    .slice(0, 128)
    .map((nodeId) => String(nodeId || '').trim())
    .filter((nodeId) => nodeId && nodeId !== nextRoot.id)
    .forEach((nodeId) => {
      nextRoot = removeNodeById(nextRoot, nodeId);
    });

  (Array.isArray(generated?.insertions) ? generated.insertions : [])
    .slice(0, 128)
    .forEach((insertion) => {
      const targetId = String(insertion?.targetId || '').trim();
      const position = PATCH_POSITIONS.has(insertion?.position)
        ? insertion.position
        : 'child';
      const node = sanitizeNode(insertion?.node);
      if (!targetId || !findNodeById(nextRoot, targetId) || !node) return;

      const existingIds = collectSubtreeIds(nextRoot);
      const insertedIds = collectSubtreeIds(node);
      if ([...insertedIds].some((nodeId) => existingIds.has(nodeId))) return;
      nextRoot = insertNode(nextRoot, targetId, node, position);
    });

  (Array.isArray(generated?.moves) ? generated.moves : [])
    .slice(0, 128)
    .forEach((operation) => {
      const nodeId = String(operation?.nodeId || '').trim();
      const targetId = String(operation?.targetId || '').trim();
      const position = PATCH_POSITIONS.has(operation?.position)
        ? operation.position
        : 'child';
      if (!nodeId || !targetId) return;
      nextRoot = moveNode(nextRoot, nodeId, targetId, position);
    });

  const generatedClassDocument = normalizeAiFontDefinitions(validateEditorDocument({
    ...createEmptyEditorDocument(),
    classes: generated?.classes || {},
  }).document);
  const classes = {
    ...(nextDocument.classes || {}),
    ...generatedClassDocument.classes,
  };
  const removeClasses = new Set(
    (Array.isArray(generated?.removeClasses) ? generated.removeClasses : [])
      .slice(0, 128)
      .map((className) => String(className || '').trim())
      .filter(Boolean)
  );
  removeClasses.forEach((className) => delete classes[className]);
  if (removeClasses.size) nextRoot = removeClassAssignments(nextRoot, removeClasses);

  return validateAndRepairEditorJson({
    ...nextDocument,
    root: nextRoot,
    classes,
  });
};

export const editSelectedElement = async ({
  prompt,
  editorDocument,
  selectedNodeId,
  images = [],
  projects = [],
  skills = [],
  jobs = [],
  onRetry,
} = {}) => {
  const currentDocument = validateAndRepairEditorJson(editorDocument || createEmptyEditorDocument());
  const selectedNode = findNodeById(currentDocument.root, selectedNodeId);

  if (!selectedNode) {
    return currentDocument;
  }
  const effectiveImages = mergeImageContext(selectedNode, images);

  const generated = await requestEditorJsonWithRetry({
    buildPrompt: (retry) => buildFocusedElementEditPrompt({
      prompt,
      selectedNode,
      classDefinitions: currentDocument.classes,
      images,
      projects,
      skills,
      jobs,
      retry,
    }),
    maxOutputTokens: HTML_EDITOR_FOCUSED_MAX_OUTPUT_TOKENS,
    retryMaxOutputTokens: HTML_EDITOR_MAX_OUTPUT_TOKENS,
    generationConfig: {
      thinkingConfig: {
        thinkingLevel: ThinkingLevel.LOW,
      },
    },
    fallbackGenerationConfig: {
      thinkingConfig: { thinkingLevel: ThinkingLevel.MINIMAL },
    },
    onRetry,
  });
  if (!Array.isArray(generated?.updates) || generated?.node || generated?.root) {
    throw new Error('The AI assistant did not return a usable item edit. Try a more specific change.');
  }

  const hydrated = hydrateImageTokensInEditorJson(generated, effectiveImages);
  const nextDocument = applyFocusedEditorUpdates(currentDocument, selectedNode.id, hydrated);

  const generatedClassDocument = normalizeAiFontDefinitions(validateEditorDocument({
    ...createEmptyEditorDocument(),
    classes: hydrated.classes || {},
  }).document);
  nextDocument.classes = {
    ...(nextDocument.classes || {}),
    ...generatedClassDocument.classes,
  };

  return validateAndRepairEditorJson(
    hydrateImageTokensInEditorJson(nextDocument, effectiveImages),
    buildAiCollectionValidationContext(currentDocument, { projects, skills })
  );
};

